import { useEffect, useState } from 'react';

import {
    TEMPLATE_CARD_BODY_COLUMN_CLASS,
    TEMPLATE_CARD_CLASS,
    TEMPLATE_CARD_END_CLASS,
    TEMPLATE_CARD_FOOTER_ACTIONS_CLASS,
    TEMPLATE_CARD_FOOTER_CLASS,
    TEMPLATE_CARD_HEADER_CLASS,
    TEMPLATE_CARD_SUBTITLE_CLASS,
    TEMPLATE_CARD_TITLE_CLASS,
    TEMPLATE_OUTLINE_BUTTON_SM_CLASS,
    TEMPLATE_PRIMARY_BUTTON_SM_CLASS,
    TEMPLATE_STATUS_CLASS,
    TEMPLATE_TOKEN_CLASS,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/template_theme';
import { Input } from '@apps/web/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import { Spinner } from '@apps/web/components/ui/spinner';
import { StatusBadge } from '@apps/web/components/ui/status_badge';
import { Switch } from '@apps/web/components/ui/switch';

/**
 * A single organization setting rendered as a live control inside the Phoebe
 * sidebar chat. When the agent identifies that a request maps to a setting, it
 * surfaces that setting here so the user can review an edit before saving it.
 *
 * This is the presentational layer only: it renders the app's real
 * `SettingsRow` + `Switch`/`Select`/`Input` and reports edits via
 * `onValueChange`. The agent-tool payload → props mapping and the actual
 * settings write (plus driving `status`) are the caller's responsibility.
 *
 * Guardrail — agent ≤ UI: only surface a field that already has a Settings-page
 * editor, so a card can never leave an org in a state the user could not set or
 * undo themselves.
 */

/** The control to render, discriminated by `type`. */
export type InlineSettingControl =
    | {
          readonly type: 'toggle';
          readonly value: boolean;
      }
    | {
          readonly type: 'select';
          readonly value: string;
          readonly options: ReadonlyArray<{
              readonly value: string;
              readonly label: string;
          }>;
      }
    | {
          readonly type: 'number';
          readonly value: number;
          readonly min?: number;
          readonly max?: number;
      };

/** Save lifecycle for the status token; the caller drives the transitions. */
export type InlineSettingStatus = 'idle' | 'saving' | 'updated' | 'error';

export type InlineSettingCardProps = {
    readonly title: string;
    readonly description?: string;
    readonly control: InlineSettingControl;
    readonly status?: InlineSettingStatus;
    readonly disabled?: boolean;
    /** Fired after the user confirms the staged value. */
    readonly onValueChange: (value: boolean | string | number) => void;
};

function NumberControl({
    control,
    disabled,
    status,
    onValueChange,
}: {
    readonly control: Extract<InlineSettingControl, { type: 'number' }>;
    readonly disabled: boolean;
    readonly status: InlineSettingStatus;
    readonly onValueChange: (value: number) => void;
}) {
    const [input, setInput] = useState(String(control.value));
    // A save round-trip (or another editor) may change the value; drop the
    // local draft once the committed value moves.
    useEffect(() => {
        setInput(String(control.value));
    }, [control.value]);
    // On a failed save the committed value never moves, so the effect above
    // won't fire — snap the draft back to the real value so the input can't
    // keep showing an uncommitted number as if it saved.
    useEffect(() => {
        if (status === 'error') {
            setInput(String(control.value));
        }
    }, [status, control.value]);

    function commit() {
        const parsed = Number.parseInt(input, 10);
        if (Number.isNaN(parsed)) {
            setInput(String(control.value));
            return;
        }
        let next = parsed;
        if (control.min !== undefined) {
            next = Math.max(control.min, next);
        }
        if (control.max !== undefined) {
            next = Math.min(control.max, next);
        }
        setInput(String(next));
        if (next !== control.value) {
            onValueChange(next);
        }
    }

    return (
        <Input
            type="number"
            className="h-8 w-20 text-right"
            min={control.min}
            max={control.max}
            disabled={disabled}
            value={input}
            onChange={(event) => setInput(event.target.value)}
            onBlur={commit}
            onKeyDown={(event) => {
                if (event.key === 'Enter') {
                    event.currentTarget.blur();
                }
            }}
        />
    );
}

function SettingControl({
    control,
    disabled,
    status,
    onValueChange,
}: {
    readonly control: InlineSettingControl;
    readonly disabled: boolean;
    readonly status: InlineSettingStatus;
    readonly onValueChange: (value: boolean | string | number) => void;
}) {
    switch (control.type) {
        case 'toggle':
            return (
                <Switch
                    checked={control.value}
                    disabled={disabled}
                    onCheckedChange={onValueChange}
                />
            );
        case 'select':
            return (
                <Select
                    value={control.value}
                    disabled={disabled}
                    onValueChange={onValueChange}
                >
                    <SelectTrigger className="h-8 w-40">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {control.options.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                                {option.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            );
        case 'number':
            return (
                <NumberControl
                    control={control}
                    disabled={disabled}
                    status={status}
                    onValueChange={onValueChange}
                />
            );
        default: {
            const exhaustive: never = control;
            throw new Error(`Unhandled control: ${JSON.stringify(exhaustive)}`);
        }
    }
}

function withControlValue(
    control: InlineSettingControl,
    value: boolean | string | number,
): InlineSettingControl {
    switch (control.type) {
        case 'toggle':
            return {
                ...control,
                value: typeof value === 'boolean' ? value : control.value,
            };
        case 'select':
            return {
                ...control,
                value: typeof value === 'string' ? value : control.value,
            };
        case 'number':
            return {
                ...control,
                value: typeof value === 'number' ? value : control.value,
            };
        default: {
            const exhaustive: never = control;
            throw new Error(`Unhandled control: ${JSON.stringify(exhaustive)}`);
        }
    }
}

function controlDisplayValue(control: InlineSettingControl): string {
    switch (control.type) {
        case 'toggle':
            return control.value ? 'On' : 'Off';
        case 'select':
            return (
                control.options.find((option) => option.value === control.value)
                    ?.label ?? control.value
            );
        case 'number':
            return String(control.value);
        default: {
            const exhaustive: never = control;
            throw new Error(`Unhandled control: ${JSON.stringify(exhaustive)}`);
        }
    }
}

export function InlineSettingCard({
    title,
    description,
    control,
    status = 'idle',
    disabled = false,
    onValueChange,
}: InlineSettingCardProps) {
    const isBusy = disabled || status === 'saving';
    const committedValue = control.value;
    const [draftValue, setDraftValue] = useState(committedValue);
    const [undoValue, setUndoValue] = useState<
        boolean | string | number | null
    >(null);
    const [submittedValue, setSubmittedValue] = useState<
        boolean | string | number | null
    >(null);

    useEffect(() => {
        setDraftValue(committedValue);
    }, [committedValue]);

    const rawDirty = draftValue !== committedValue;
    // A successful write can finish before the organization cache delivers
    // the new value. Keep the submitted value as the visible applied state so
    // the card does not flash back to “Unsaved changes” during that gap.
    const showsSubmittedStatus =
        submittedValue !== null && submittedValue === draftValue;
    const showApplied =
        status === 'updated' && (!rawDirty || showsSubmittedStatus);
    const showError = status === 'error' && (!rawDirty || showsSubmittedStatus);
    const isDirty = rawDirty && !showApplied;
    const draftControl = withControlValue(control, draftValue);

    function saveDraft() {
        if (!isDirty || isBusy) {
            return;
        }
        setUndoValue(committedValue);
        setSubmittedValue(draftValue);
        onValueChange(draftValue);
    }

    function discardDraft() {
        setDraftValue(committedValue);
    }

    function undoChange() {
        if (undoValue === null || isBusy) {
            return;
        }
        setDraftValue(undoValue);
        onValueChange(undoValue);
        setUndoValue(null);
    }

    return (
        <div className={TEMPLATE_CARD_CLASS}>
            <div className={TEMPLATE_CARD_HEADER_CLASS}>
                <div className="min-w-0">
                    <p className={TEMPLATE_CARD_TITLE_CLASS}>{title}</p>
                    {description ? (
                        <p className={TEMPLATE_CARD_SUBTITLE_CLASS}>
                            {description}
                        </p>
                    ) : null}
                </div>
                <div className={TEMPLATE_CARD_END_CLASS}>
                    <span className={TEMPLATE_TOKEN_CLASS}>
                        Phoebe settings
                    </span>
                </div>
            </div>
            <div className={TEMPLATE_CARD_BODY_COLUMN_CLASS}>
                <div className="grid grid-cols-[130px_minmax(0,1fr)] items-center gap-2.5 text-[13px]">
                    <p className="text-xs text-[#617578]">Value</p>
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                        <span className="text-[#829494] line-through decoration-[rgba(224,0,61,0.45)]">
                            {controlDisplayValue(control)}
                        </span>
                        <span className="text-xs text-[#829494]">→</span>
                        <SettingControl
                            control={draftControl}
                            disabled={isBusy}
                            status={status}
                            onValueChange={setDraftValue}
                        />
                    </div>
                </div>
            </div>
            <div className={TEMPLATE_CARD_FOOTER_CLASS}>
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                    {status === 'saving' ? <Spinner size="xs" /> : null}
                    {showApplied ? (
                        <StatusBadge
                            status={{ label: 'Applied', intent: 'positive' }}
                            className={TEMPLATE_STATUS_CLASS.positive}
                        />
                    ) : showError ? (
                        <StatusBadge
                            status={{
                                label: 'Couldn’t save',
                                intent: 'negative',
                            }}
                            className={TEMPLATE_STATUS_CLASS.negative}
                        />
                    ) : isDirty ? (
                        <StatusBadge
                            status={{
                                label: 'Unsaved changes',
                                intent: 'warning',
                            }}
                            className={TEMPLATE_STATUS_CLASS.warning}
                        />
                    ) : null}
                    <p className="text-xs text-[#617578]">
                        {status === 'saving'
                            ? 'Applying change…'
                            : showApplied
                              ? 'Applied just now'
                              : showError
                                ? 'Review the value and try again.'
                                : isDirty
                                  ? 'Not applied to Phoebe settings yet'
                                  : 'Edit the value, then save the change.'}
                    </p>
                </div>
                <div className={TEMPLATE_CARD_FOOTER_ACTIONS_CLASS}>
                    {isDirty ? (
                        <button
                            type="button"
                            className={TEMPLATE_OUTLINE_BUTTON_SM_CLASS}
                            disabled={isBusy}
                            onClick={discardDraft}
                        >
                            Discard
                        </button>
                    ) : showApplied && undoValue !== null ? (
                        <button
                            type="button"
                            className={TEMPLATE_OUTLINE_BUTTON_SM_CLASS}
                            disabled={isBusy}
                            onClick={undoChange}
                        >
                            Undo
                        </button>
                    ) : null}
                    {isDirty ? (
                        <button
                            type="button"
                            className={TEMPLATE_PRIMARY_BUTTON_SM_CLASS}
                            disabled={isBusy}
                            onClick={saveDraft}
                        >
                            Save changes
                        </button>
                    ) : null}
                </div>
            </div>
        </div>
    );
}
